/**
 * Wires the app's long-lived services together: the document store with
 * autosave, device settings, toasts and transient view state. Dependencies are
 * injected so the whole app can be mounted in tests.
 */
import type { IdGen } from '../model/ids';
import { randomIdGen, systemNow } from '../model/ids';
import { parseDoc } from '../model/schema';
import type { LoadStatus } from '../state/persistence';
import { debounce, DOC_KEY, loadDoc, saveDoc, type Timers } from '../state/persistence';
import { SettingsStore } from '../state/settings.svelte';
import type { StorageLike } from '../state/storage';
import { DocStore } from '../state/store.svelte';
import { strings } from '../strings';
import { Toasts } from '../ui/toasts.svelte';
import { Importer } from './importer.svelte';

export const AUTOSAVE_DELAY_MS = 300;

export interface AppDeps {
  storage: StorageLike | null;
  newId?: IdGen;
  now?: () => Date;
  timers?: Timers;
  /** Window for lifecycle and cross-tab events; omit in tests that don't need them. */
  window?: Window;
}

/** UI state that is neither part of the list nor a saved preference. */
export class ViewState {
  query = $state('');
  editMode = $state(false);
  sharing = $state(false);
  importing = $state(false);
  about = $state(false);
  /** Text for the assertive live region (reorder announcements). */
  announcement = $state('');

  announce(message: string): void {
    // Re-announce identical messages by clearing first.
    this.announcement = '';
    queueMicrotask(() => {
      this.announcement = message;
    });
  }
}

export interface AppServices {
  store: DocStore;
  settings: SettingsStore;
  toasts: Toasts;
  view: ViewState;
  importer: Importer;
  loadStatus: LoadStatus;
  /** For hydrating imported data. */
  newId: IdGen;
  now: () => Date;
  /** Writes any pending change immediately. */
  flush: () => void;
  destroy: () => void;
}

export function createServices(deps: AppDeps): AppServices {
  const newId = deps.newId ?? randomIdGen();
  const now = deps.now ?? systemNow;
  const toasts = new Toasts(deps.timers);
  const { doc, status } = loadDoc(deps.storage, { newId, now });

  let warnedSaveFailure = false;
  const autosave = debounce(
    () => {
      if (saveDoc(deps.storage, store.doc) || warnedSaveFailure) return;
      warnedSaveFailure = true;
      toasts.show(strings.saveFailed);
    },
    AUTOSAVE_DELAY_MS,
    deps.timers,
  );

  const store = new DocStore({
    initial: doc,
    newId,
    now,
    onChange: () => {
      autosave.schedule();
    },
  });

  if (status === 'recovered') toasts.show(strings.loadRecovered);

  const flush = () => {
    autosave.flush();
  };
  const onVisibility = () => {
    if (deps.window?.document.visibilityState === 'hidden') flush();
  };
  // Another tab saved: adopt its list (last writer wins, our pending save is dropped).
  const onStorage = (event: StorageEvent) => {
    if (event.key !== DOC_KEY || event.newValue === null) return;
    let incoming: unknown;
    try {
      incoming = JSON.parse(event.newValue);
    } catch {
      return;
    }
    const parsed = parseDoc(incoming);
    if (!parsed || parsed.updatedAt === store.doc.updatedAt) return;
    autosave.cancel();
    store.adopt(parsed);
    toasts.show(strings.updatedInOtherTab);
  };

  deps.window?.addEventListener('pagehide', flush);
  deps.window?.addEventListener('visibilitychange', onVisibility);
  deps.window?.addEventListener('storage', onStorage);

  return {
    store,
    settings: new SettingsStore(deps.storage),
    toasts,
    view: new ViewState(),
    importer: new Importer(store, toasts),
    loadStatus: status,
    newId,
    now,
    flush,
    destroy: () => {
      flush();
      deps.window?.removeEventListener('pagehide', flush);
      deps.window?.removeEventListener('visibilitychange', onVisibility);
      deps.window?.removeEventListener('storage', onStorage);
    },
  };
}
